import { css, keyframes, SerializedStyles } from '@emotion/react';
import { theme } from 'global';

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(250%);
  }
`;

const verificationPulse = keyframes`
  0% {
    width: 8%;
  }

  100% {
    width: 92%;
  }
`;

export const page = css`
  min-height: fit-content;
  background: ${theme.colors.white};
  display: flex;
  justify-content: center;
  padding: 32px 40px;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    padding: 24px 16px 96px;
  }
`;

export const panel = css`
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    gap: 24px;
  }
`;

export const section = css`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    gap: 16px;
  }
`;

export const title = css`
  margin: 0;
  color: ${theme.colors.secondary};
`;

export const progressTrack = css`
  width: 100%;
  height: 6px;
  background: ${theme.colors.neutral[100]};
  overflow: hidden;
`;

export const progressFill = (percent: number): SerializedStyles => css`
  width: ${percent}%;
  height: 100%;
  background: ${theme.colors.primary.main};
  transition: width 200ms ease;
`;

export const intro = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const introTitle = css`
  margin: 0;
  color: ${theme.colors.secondary};
`;

export const introBody = css`
  margin: 0;
  color: ${theme.colors.neutral[600]};
`;

export const selectIdentityDocumentTypeContent = css`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    gap: 20px;
  }
`;

export const optionList = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const optionCard = (selected: boolean): SerializedStyles => css`
  appearance: none;
  width: 100%;
  border: 1px solid
    ${selected ? theme.colors.primary.medium : theme.colors.neutral[200]};
  background: ${selected ? theme.colors.primary.lightest : theme.colors.white};
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary.medium};
    background: ${theme.colors.neutral[0]};
    span:first-of-type svg {
      color: ${theme.colors.primary.medium};
    }
  }
`;

export const optionIcon = css`
  flex: 0 0 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.secondary};
`;

export const optionCopy = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const optionTitle = css`
  margin: 0;
  color: ${theme.colors.neutral[600]};
`;

export const optionDescription = css`
  margin: 0;
  color: ${theme.colors.neutral[500]};
`;

export const infoBanner = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${theme.colors.info};
  border-radius: 4px;
  background: ${theme.colors.infoLight};
`;

export const infoIcon = css`
  flex: 0 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.info};
  margin-top: 1px;
`;

export const infoCopy = css`
  margin: 0;
  color: ${theme.colors.neutral[600]};
`;

export const errorBanner = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${theme.colors.danger};
  border-radius: 4px;
  background: ${theme.colors.dangerLight};
`;

export const errorIcon = css`
  flex: 0 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`;

export const errorCopy = css`
  margin: 0;
  color: ${theme.colors.neutral[600]};
`;

export const uploadContentGroup = css`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    gap: 16px;
  }
`;

export const uploaderWrapper = css`
  width: 100%;

  > div {
    width: 100%;
    max-width: none;
    margin: 0;
  }
`;

export const actionRow = css`
  // This is to offset gap: 40px at "panel", so we use built-in margin from NavButtons component instead
  margin-top: -40px;
`;

export const confirmPage = css`
  @media screen and (max-width: ${theme.breaks.xs}px) {
    min-height: fit-content;
    padding: 24px 16px 96px;
  }
`;

export const confirmHeader = css`
  gap: 20px;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    gap: 12px;
  }
`;

export const confirmForm = css`
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    gap: 24px;
  }
`;

export const formSection = css`
  display: flex;
  flex-direction: column;
  gap: 21px;
`;

export const formSectionHeader = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const formSectionTitle = css`
  margin: 0;
  color: ${theme.colors.secondary};
`;

export const formStack = css`
  display: flex;
  flex-direction: column;
  gap: 21px;
`;

export const formRow = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
`;

export const addressField = css`
  .MuiInputBase-root {
    align-items: center;
  }
`;

export const addressHint = css`
  margin: 4px 0 0;
  color: ${theme.colors.neutral[500]};
`;

export const manualAddressLink = css`
  && {
    min-width: 0;
    padding: 0;
    color: ${theme.colors.primary.medium};
    line-height: inherit;
    vertical-align: baseline;
  }
`;

export const overlay = css`
  position: absolute;
  inset: 56px 0 0;
  background: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  height: 100vh;
  z-index: 1100;
`;

export const verificationCard = css`
  width: 100%;
  max-width: 472px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 40px;
  border-radius: 12px;
  background: ${theme.colors.white};

  @media screen and (max-width: ${theme.breaks.xs}px) {
    padding: 32px 24px;
  }
`;

export const verificationIllustration = css`
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const verificationIllustrationImage = css`
  display: block;
  width: 96px;
  height: 96px;
`;

export const verificationContent = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

export const verificationTitle = css`
  margin: 0;
  color: ${theme.colors.secondary};
  text-align: center;
`;

export const verificationTrack = css`
  width: 100%;
  height: 6px;
  background: ${theme.colors.neutral[100]};
  overflow: hidden;
  position: relative;
`;

export const verificationFill = css`
  height: 100%;
  background: ${theme.colors.primary.main};
  animation: ${verificationPulse} 2.5s ease-out forwards;
`;

export const cancelButton = css`
  && {
    padding: 11px 0 12px;
    color: ${theme.colors.primary.medium};
  }
`;

export const successSkeleton = (height: number): SerializedStyles => css`
  width: 100%;
  height: ${height}px;
  background: ${theme.colors.neutral[50]};
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 40%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${theme.colors.neutral[200]} 50%,
      transparent 100%
    );
    animation: ${shimmer} 1.4s ease-in-out infinite;
  }
`;

export const successSection = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const fieldHelperText = css`
  display: flex;
  align-items: baseline;
  margin: 4px 0 0 0 !important;
  color: ${theme.colors.danger};

  .errorIcon {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
    position: relative;
    top: 3px;
    margin-right: 4px;
    fill: ${theme.colors.danger};
  }
`;

export const textfield = css`
  width: 100%;
  margin: 0;

  .MuiFormControl-root {
    margin: 0;
  }

  .MuiFormHelperText-root.Mui-error {
    ${fieldHelperText};
  }

  .errorRow {
    display: flex;
    align-items: baseline;
    color: ${theme.colors.danger};
    font-size: ${theme.fontSizes.body4};

    .errorIcon {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
      position: relative;
      top: 3px;
      margin-right: 4px;
      fill: ${theme.colors.danger};
    }
  }
`;
